/**
 * timeOverlap.js — shared pure time-range math for "HH:MM" clock times.
 *
 * Seeded from the duplicated helpers in BookingDetailModal.js (timeToMinutes /
 * addMinutes / the interval-overlap test) and EditTravelSlotsModal.js
 * (checkForOverlaps). Keep this module PURE — no React, no domain objects.
 * Callers map their domain rows (bookings, availability slots) to plain
 * { startTime, endTime } ranges and pass them in.
 */

/** "HH:MM" -> minutes since midnight. */
export const timeToMinutes = (t) => {
  const [h, m] = String(t).split(':').map(Number);
  return h * 60 + (m || 0);
};

/** minutes since midnight -> "HH:MM" (clamped to a single day). */
export const minutesToTime = (mins) => {
  const clamped = Math.max(0, Math.min(mins, 24 * 60));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** Shift a "HH:MM" time by a signed number of minutes. */
export const addMinutes = (t, delta) => minutesToTime(timeToMinutes(t) + delta);

/**
 * Do two half-open intervals [aStart, aEnd) and [bStart, bEnd) overlap?
 * Half-open means adjacency does NOT overlap (10:00-11:00 and 11:00-12:00 are fine).
 */
export const rangesOverlap = (aStart, aEnd, bStart, bEnd) =>
  timeToMinutes(aStart) < timeToMinutes(bEnd) && timeToMinutes(aEnd) > timeToMinutes(bStart);

/**
 * Return every range in `ranges` that overlaps `range`.
 * `range` and each entry of `ranges` must have { startTime, endTime }; extra
 * fields on the entries are preserved on the returned items.
 */
export const findOverlaps = (range, ranges = []) =>
  (ranges || []).filter((r) => rangesOverlap(range.startTime, range.endTime, r.startTime, r.endTime));

const DAY_END = 24 * 60;

/**
 * Generate a consecutive group of equal-duration slots from a start time.
 * Slots that would overlap the optional lunch window are skipped (the cursor jumps
 * to the end of lunch and the skipped slot does NOT count against `count`). Stops
 * early if a slot would run past midnight.
 *
 * @param {string} start - "HH:MM" start of the first slot
 * @param {number} duration - minutes per slot
 * @param {number} count - number of slots to produce
 * @param {{ start: string, duration: number }|null} lunch - optional lunch window to skip
 * @returns {Array<{ startTime, endTime, slotDuration }>}
 */
export const generateSlotGroup = (start, duration, count, lunch = null) => {
  const out = [];
  const lunchStart = lunch ? timeToMinutes(lunch.start) : null;
  const lunchEnd = lunch ? lunchStart + lunch.duration : null;

  let cursor = timeToMinutes(start);
  let made = 0;
  // Guard against an unproductive loop (e.g. lunch fully blocking) — bounded by the day.
  let safety = 0;
  while (made < count && safety < 24 * 4) {
    safety += 1;
    const slotEnd = cursor + duration;
    if (slotEnd > DAY_END) break;

    if (lunch && cursor < lunchEnd && slotEnd > lunchStart) {
      cursor = lunchEnd; // jump past lunch; this slot doesn't count
      continue;
    }

    out.push({
      startTime: minutesToTime(cursor),
      endTime: minutesToTime(slotEnd),
      slotDuration: duration,
    });
    cursor = slotEnd;
    made += 1;
  }
  return out;
};

/**
 * Plan an "up to gap" nudge: insert newSlots and shift ONLY the colliding chain of
 * existing slots forward by the minimum needed, stopping at the first gap that
 * absorbs the shift (a lunch break is just such a gap — it isn't a slot here).
 *
 * newSlots:      [{ startTime, endTime, slotDuration }] — the slots being added
 * existingSlots: [{ startTime, endTime?, slotDuration?/duration?, isBooked?, source? }]
 *
 * Returns { shifts, blockedReason }:
 *  - shifts: [{ oldStartTime, newStartTime, newEndTime, slotDuration }] (empty if no move needed)
 *  - blockedReason: null, or { type: 'booked'|'lunch'|'frontOverlap'|'endofday', time }
 *    when the cascade can't be resolved by shifting forward.
 */
export const planNudge = (newSlots = [], existingSlots = []) => {
  if (!newSlots.length) return { shifts: [], blockedReason: null };

  const newStart = Math.min(...newSlots.map((s) => timeToMinutes(s.startTime)));
  const newEnd = Math.max(...newSlots.map((s) => timeToMinutes(s.endTime)));
  const newStartSet = new Set(newSlots.map((s) => s.startTime));

  const sorted = (existingSlots || [])
    .filter((s) => s && s.startTime)
    .map((s) => {
      const start = timeToMinutes(s.startTime);
      const dur = s.slotDuration || s.duration ||
        (s.endTime ? timeToMinutes(s.endTime) - start : 60);
      return {
        startTime: s.startTime,
        endTime: s.endTime || minutesToTime(start + dur),
        slotDuration: dur,
        isBooked: !!s.isBooked,
        source: s.source || 'recurring',
      };
    })
    .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

  const shifts = [];
  let cursor = newEnd; // following slots must start at/after this

  for (const e of sorted) {
    const eStart = timeToMinutes(e.startTime);
    const eEnd = timeToMinutes(e.endTime);

    if (newStartSet.has(e.startTime)) continue; // same start = replace, not a shift
    if (eEnd <= newStart) continue; // entirely before the new block — untouched
    if (eStart < newStart) return { shifts: [], blockedReason: { type: 'frontOverlap', time: e.startTime } };
    if (eStart >= cursor) break; // a gap absorbs the shift — stop cascading

    // e overlaps and sits after the new block start → must move forward
    if (e.isBooked) return { shifts: [], blockedReason: { type: 'booked', time: e.startTime } };
    if (e.source === 'lunch') return { shifts: [], blockedReason: { type: 'lunch', time: e.startTime } };
    const ns = cursor;
    const ne = cursor + e.slotDuration;
    if (ne > DAY_END) return { shifts: [], blockedReason: { type: 'endofday', time: e.startTime } };

    shifts.push({
      oldStartTime: e.startTime,
      newStartTime: minutesToTime(ns),
      newEndTime: minutesToTime(ne),
      slotDuration: e.slotDuration,
    });
    cursor = ne;
  }

  return { shifts, blockedReason: null };
};

/**
 * Forward-pack a day's slots by their DESIRED start (after the user's per-slot shifts):
 * resolve overlaps by pushing a slot to start no earlier than the previous slot's end,
 * but leave a slot alone if it already starts at/after that end (up-to-gap — a gap or
 * lunch break stops the cascade). Immovable slots (isBooked / source 'lunch') may not be
 * pushed; if one would have to move, returns a blockedReason instead of a placement.
 *
 * slots: [{ id, startTime, slotDuration, isBooked?, source?, removed? }] (removed ones ignored)
 * Returns { placements: [{ id, startTime, endTime, slotDuration }], blockedReason }
 */
export const forwardPack = (slots = []) => {
  const active = (slots || [])
    .filter((s) => s && !s.removed && s.startTime)
    .map((s) => ({
      id: s.id,
      desiredStart: timeToMinutes(s.startTime),
      slotDuration: s.slotDuration || 60,
      isBooked: !!s.isBooked,
      source: s.source || 'recurring',
    }))
    .sort((a, b) => a.desiredStart - b.desiredStart);

  const placements = [];
  let prevEnd = -Infinity;
  for (const s of active) {
    const start = Math.max(s.desiredStart, prevEnd);
    if (start > s.desiredStart && (s.isBooked || s.source === 'lunch')) {
      return { placements: [], blockedReason: { type: s.isBooked ? 'booked' : 'lunch', time: minutesToTime(s.desiredStart) } };
    }
    const end = start + s.slotDuration;
    if (end > DAY_END) {
      return { placements: [], blockedReason: { type: 'endofday', time: minutesToTime(s.desiredStart) } };
    }
    placements.push({ id: s.id, startTime: minutesToTime(start), endTime: minutesToTime(end), slotDuration: s.slotDuration });
    prevEnd = end;
  }
  return { placements, blockedReason: null };
};
